import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MessageCircle, Send, ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialThread = params.get("thread");
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(initialThread || null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!user || typeof user !== "object") { nav("/login", { state: { from: "/messages" }}); return; }
    api.get("/threads").then(({data}) => setThreads(data || []));
    // eslint-disable-next-line
  }, [user, loading]);

  useEffect(() => {
    if (!active) return;
    api.get(`/threads/${active}/messages`).then(({data}) => setMsgs(data || []));
    const t = setInterval(() => {
      api.get(`/threads/${active}/messages`).then(({data}) => setMsgs(data || []));
    }, 5000);
    return () => clearInterval(t);
  }, [active]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || !active) return;
    try {
      const { data } = await api.post(`/threads/${active}/send`, { text: input });
      setMsgs(m => [...m, data]);
      setInput("");
    } catch (e) { toast.error("Failed to send"); }
  };

  if (loading || !user || typeof user !== "object") return null;

  const activeThread = threads.find(t => t.id === active);

  return (
    <div className="bg-[#FBFAF6] min-h-screen" data-testid="messages-page">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-10">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={()=>nav(-1)} className="p-2 rounded-full hover:bg-gray-100 transition" aria-label="back"><ArrowLeft size={20}/></button>
          <h1 className="font-display font-black text-3xl text-[#0E1F18]">Messages</h1>
        </div>

        <div className="grid lg:grid-cols-[320px_1fr] gap-4 h-[calc(100vh-220px)] min-h-[500px]">
          {/* Thread list */}
          <aside className="bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col" data-testid="thread-list">
            <div className="p-4 border-b border-gray-100">
              <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Conversations</div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {threads.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500">
                  <MessageCircle size={32} className="mx-auto text-gray-300 mb-2"/>
                  No conversations yet. Start one from a PG page or Roommates.
                </div>
              ) : threads.map(t => (
                <button key={t.id} onClick={()=>setActive(t.id)}
                  className={`w-full text-left p-4 border-b border-gray-50 hover:bg-emerald-50/50 transition flex items-center gap-3 ${active === t.id ? "bg-emerald-50/70 border-l-4 border-l-emerald-500" : ""}`}
                  data-testid="thread-item" data-thread-id={t.id}>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 grid place-items-center text-white font-bold">
                    {(t.other_user?.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-[#0E1F18] truncate">{t.other_user?.name || "Unknown"}</div>
                    <div className="text-xs text-gray-500 capitalize">{t.other_user?.role || ""}</div>
                  </div>
                </button>
              ))}
            </div>
          </aside>

          {/* Conversation pane */}
          <main className="bg-white rounded-2xl border border-gray-200 flex flex-col overflow-hidden">
            {active && activeThread ? (
              <>
                <div className="p-4 border-b border-gray-100 flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-white">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 grid place-items-center text-white font-bold">
                    {(activeThread.other_user?.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="font-display font-black text-lg text-[#0E1F18]">{activeThread.other_user?.name || "Unknown"}</div>
                    <div className="text-xs text-gray-500 capitalize flex items-center gap-1.5">
                      <Lock size={11}/> Masked chat · phone numbers private
                    </div>
                  </div>
                </div>
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-gradient-to-b from-gray-50/40 to-white" data-testid="conversation-messages">
                  {msgs.map((m, i) => {
                    const mine = m.sender_id === user.id;
                    return (
                      <div key={m.id || i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                          mine ? "bg-emerald-600 text-white rounded-br-sm" : "bg-white border border-gray-200 text-[#0E1F18] rounded-bl-sm shadow-sm"
                        }`}>
                          {m.text}
                        </div>
                      </div>
                    );
                  })}
                  {msgs.length === 0 && (
                    <div className="text-center text-sm text-gray-400 mt-12">Start the conversation 👋</div>
                  )}
                </div>
                <form onSubmit={send} className="p-3 border-t border-gray-100 flex gap-2">
                  <input
                    value={input}
                    onChange={e=>setInput(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm"
                    data-testid="message-input"
                  />
                  <button type="submit" disabled={!input.trim()} className="w-10 h-10 grid place-items-center rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white transition" data-testid="message-send">
                    <Send size={16}/>
                  </button>
                </form>
              </>
            ) : (
              <div className="flex-1 grid place-items-center text-center text-gray-400">
                <div>
                  <MessageCircle size={48} className="mx-auto mb-3 text-gray-300"/>
                  <div className="font-bold text-[#0E1F18]">Pick a conversation</div>
                  <div className="text-sm">Or start a new one from a PG / Roommates page.</div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
